<script setup lang="ts">

enum FormOrder {
  result = 1,
  form = 2
}
const emit = defineEmits(['changed', 'setValue']);
const props = defineProps({
  isAuth: {
    type: Boolean,
    default: true
  },
  legalEntitySelected: {
    type: Boolean,
    default: false
  }
})

const { getLegalEntityData, setLegalEntityData } = paymentLegalEntityDataCookie()
const formCookie = getLegalEntityData()
const form = ref({
  email: '',
  phone_number: '+7',
  name: '',
  bank: '',
  address: '',
  bin: '',
  account_number: '',
  bic: '',
})

if (formCookie.value) {
  form.value = {
    ...formCookie.value
  }
}
const formErrors = ref({
  email: '',
  phone_number: '',
  name: '',
  bank: '',
  address: '',
  bin: '',
  account_number: '',
  bic: '',
})
const {t} = useI18n()
const formOrder = ref(FormOrder.form)
function clearFormErrors() {
  formErrors.value = {
    email: '',
    phone_number: '',
    name: '',
    bank: '',
    address: '',
    bin: '',
    account_number: '',
    bic: '',
  }
}

function validateForm()
{
  const formValue = form.value
  const validated = ref(true)
  if (formValue.phone_number.length < 9) {
    formErrors.value.phone_number = t('validation.validatePhoneNumber')
    validated.value = false
  }
  if (!formValue.email) {
    formErrors.value.email = t('validation.required.just').replace(':attribute', t('contacts.contacts_page.email'))
    validated.value = false
  }
  if (!formValue.name) {
    formErrors.value.name = t('validation.required.just').replace(':attribute', t('legal_entity.name'))
    validated.value = false
  }
  if (!formValue.bin) {
    formErrors.value.bin = t('validation.required.just').replace(':attribute', t('legal_entity.bin'))
    validated.value = false
  }
  if (formValue.bin && formValue.bin.length < 12) {
    formErrors.value.bin = t('validation.min.string_attr').replace(':attribute', t('legal_entity.bin')).replace(':limit', '12')
    validated.value = false
  }
  if (!formValue.address) {
    formErrors.value.address = t('validation.required.just').replace(':attribute', t('legal_entity.address'))
    validated.value = false
  }
  if (!formValue.bank) {
    formErrors.value.bank = t('validation.required.just').replace(':attribute', t('legal_entity.bank'))
    validated.value = false
  }
  if (!formValue.account_number) {
    formErrors.value.account_number = t('validation.required.just').replace(':attribute', t('legal_entity.account_number'))
    validated.value = false
  }
  if (formValue.account_number && formValue.account_number.length < 16) {
    formErrors.value.account_number = t('validation.min.string_attr').replace(':attribute', t('legal_entity.account_number')).replace(':limit', '16')
    validated.value = false
  }
  if (!formValue.bic) {
    formErrors.value.bic = t('validation.required.just').replace(':attribute', t('legal_entity.bic'))
    validated.value = false
  }
  return validated.value
}
const {$toast, $toastError} = useNuxtApp()

async function createLegalEntity(){
  clearFormErrors()
  const validated =  validateForm()
  if (!validated) {
    return
  }
  const body = {
    ...form.value,
    phone_number: removePhoneMask(form.value.phone_number)
  }

  if (!props.isAuth) {
    return
  }
  const {data: response, error, pending} = await createLegalEntities(body)
  if (error.value) {
    Object.entries(error.value.data.errors).forEach((err) => {
      if (err[0]) {
        formErrors.value[err[0]] = err[1][0]
      }
    })
  }
  if (response.value) {
    $toast(t('messages.success.store'))
    emit('changed')
  }
}
const handleTyping = (key:string) => {
  formErrors.value[key] = ''
}

function setFormValue() {
  clearFormErrors()
  const validated =  validateForm()
  if (!validated) {
    return
  }
  formOrder.value = FormOrder.result
  emit('setValue', form.value)
  setLegalEntityData(form.value)
}
function changeData() {
  formOrder.value = FormOrder.form
  emit('setValue', null)
}
</script>

<template>
  <div>
    <div v-if="formOrder === FormOrder.result" class="text-lg-d flex flex-col gap-1">
      <p v-for="(fields, key) in form"><span class="font-medium">{{ $t(`legal_entity.${key}`) }}</span>: {{ fields }}</p>

      <UIBtn :color="'white'" class="mt-5 rounded-xl w-fit" @click="changeData()">
        {{ $t('input_forms.button.change_data') }}
      </UIBtn>
    </div>
    <form v-else @submit.prevent="createLegalEntity">
      <div class="flex gap-2 flex-col">
        <div class="flex flex-row flex-wrap gap-2">
          <div>
            <UIInputAnimated
                class="bg-white drop-shadow-sm  w-full h-[54px]"
                type="email"
                :placeholder="$t('contacts.contacts_page.email')"
                id="email"
                v-model="form.email"
                :required="true"
                @input="handleTyping('email')"
                name="email"

            />
            <p v-if="formErrors.email" class="ml-2 mt-1 error-text">{{ formErrors.email }}</p>

          </div>
          <div>
            <UIInputAnimated
                class="bg-white drop-shadow-sm  w-full h-[54px]"
                type="tel"
                :placeholder="$t('contacts.contacts_page.phone')"
                mask="+7 (###) ###-##-##"
                id="phone_number"
                @input="handleTyping('phone_number')"
                v-model="form.phone_number"
                :required="true"
            />
            <p v-if="formErrors.phone_number" class="ml-2 mt-1 error-text">{{ formErrors.phone_number }}</p>
          </div>
        </div>
        <div class="gap-2 flex flex-row flex-wrap ">
          <div>
            <UIInputAnimated
                class="bg-white drop-shadow-sm  w-full h-[54px]"
                type="text"
                :placeholder="$t('legal_entity.name')"
                id="legal_name"
                v-model="form.name"
                @input="handleTyping('name')"
                :required="true"
            />
            <p v-if="formErrors.name" class="ml-2 mt-1 error-text">{{ formErrors.name }}</p>
          </div>
          <div>
            <UIInputAnimated
                class="bg-white drop-shadow-sm  w-full h-[54px]"
                type="text"
                :placeholder="$t('legal_entity.bank') "
                id="bank_name"
                v-model="form.bank"
                :required="true"
                name="bank_name"
                @input="handleTyping('bank')"
            />
            <p v-if="formErrors.bank" class="ml-2 mt-1 error-text">{{ formErrors.bank }}</p>
          </div>
        </div>
        <div class="gap-2 flex flex-row flex-wrap ">
          <div>
            <UIInputAnimated
                class="bg-white drop-shadow-sm  w-full h-[54px]  text-xs"
                type="text"
                :placeholder="$t('legal_entity.bin') "
                id="legal_name"
                mask="############"
                v-model="form.bin"
                :required="true"
                @input="handleTyping('bin')"
            />
            <p v-if="formErrors.bin" class="ml-2 mt-1 error-text">{{ formErrors.bin }}</p>

          </div>
          <div class="relative ">
            <UIInputAnimated
                class="bg-white drop-shadow-sm  w-full h-[54px] text-base"
                type="text"
                id="legal_name"
                :placeholder="$t('legal_entity.address')"
                v-model="form.address"
                :required="true"
                @input="handleTyping('address')"
            />
            <p v-if="formErrors.address" class="ml-2 mt-1 error-text">{{ formErrors.address }}</p>

          </div>
        </div>
        <div class="gap-2 flex flex-row flex-wrap ">
          <div>
            <UIInputAnimated
                class="bg-white drop-shadow-sm  w-full  h-[54px]  text-xs"
                type="text"
                :placeholder="$t('legal_entity.account_number') "
                id="legal_name"
                v-model="form.account_number"
                mask="####################"
                :required="true"
                @input="handleTyping('account_number')"
            />
            <p v-if="formErrors.account_number" class="ml-2 mt-1 error-text">{{ formErrors.account_number }}</p>

          </div>
          <div class="relative ">
            <UIInputAnimated
                class="bg-white drop-shadow-sm  w-full h-[54px] text-base"
                type="text"
                id="legal_name"
                :placeholder="$t('legal_entity.bic')"
                v-model="form.bic"
                :required="true"
                @input="handleTyping('bic')"
            />
            <p v-if="formErrors.bic" class="ml-2 mt-1 error-text">{{ formErrors.bic }}</p>
          </div>
        </div>
      </div>
      <UIBtn v-if="isAuth" :color="'green'" type="submit" class="mt-2 rounded-full">{{
          $t('input_forms.button.save')
        }}
      </UIBtn>
      <UIBtn v-else-if="formOrder === FormOrder.form" @click="setFormValue" :color="'green'" type="submit" class="mt-2 rounded-full">{{
          $t('input_forms.button.confirm')
        }}
      </UIBtn>
    </form>
  </div>
</template>

<style scoped>

</style>